import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';

import { DataTable, useGenerateWebEDIColumns } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { DisplayModes } from 'components/DataTable/Types';

import { DocumentSectionError } from '../DocumentSectionError/DocumentSectionError';
import { WebEDISection } from '../WebEDISection/WebEDISection';

export const DocumentBaseSection = memo(({ config, mode, emptyState, isSubmitting }: DocumentBaseSectionProps) => {
  const { label: title, description, fields } = config;
  if (!fields?.length) {
    return null;
  }
  return (
    <WebEDISection header={{ title, spacing: 'medium', description }}>
      <div className="space-y-4">
        <DocumentSectionError path={config.type} sectionConfig={config} />
        <DataTableProvider name={config.type}>
          <BaseSectionTable
            config={fields}
            mode={mode}
            dataPrefix={config.type}
            emptyState={emptyState}
            isSubmitting={Boolean(isSubmitting)}
          />
        </DataTableProvider>
      </div>
    </WebEDISection>
  );
});

DocumentBaseSection.displayName = 'DocumentBaseSection';

const BaseSectionTable = ({
  mode,
  config,
  dataPrefix,
  emptyState,
  isSubmitting,
}: {
  mode: DisplayModes;
  config: ui.Section['fields'];
  dataPrefix: string;
  emptyState?: string;
  isSubmitting: boolean;
}) => {
  const { t } = useTranslation();
  const { fields } = useDataTableContext();

  const columns = useGenerateWebEDIColumns({
    config: config,
    ...TABLE_STATIC_CONFIG[mode],
    displayMode: mode,
  });

  return (
    <DataTable
      key={mode}
      meta={{ dataPrefix, mode, isSubmitting }}
      columns={columns}
      data={fields}
      enableGlobalSearch={false}
      showCellBorders={mode === DisplayModes.create || mode === DisplayModes.edit}
      emptyState={
        emptyState ??
        (mode === DisplayModes.create || mode === DisplayModes.edit
          ? t(`webedi:${dataPrefix}.empty_state.create.text`)
          : t(`webedi:${dataPrefix}.empty_state.view.text`))
      }
    />
  );
};

const TABLE_STATIC_CONFIG = {
  view: {},
  create: {},
  edit: {},
};

type DocumentBaseSectionProps = {
  config: Omit<ui.Section, 'type'> & {
    type: string;
  };
  mode: DisplayModes;
  emptyState?: string;
  isSubmitting: boolean;
};

/**
 * t('webedi:attachments.empty_state.create.text')
 * t('webedi:attachments.empty_state.view.text')
 * t('webedi:attachments.empty_state.edit.text')
 */
